import {
  Directive,
  ElementRef,
  Host,
  Self,
} from '@angular/core';

/** The default tab index value to apply to focusable elements. */
export const TABINDEX = 1;

/**
 * A directive that automatically sets the tabindex attribute to 1 for all focusable elements.
 * 
 * This directive is useful for improving keyboard navigation by ensuring focusable elements
 * have a consistent tab order. It only modifies elements that have a default tabindex of 0.
 * 
 * @example
 * ```html
 * <!-- Automatically applied to these elements -->
 * <input type="text" /> <!-- tabindex will be set to 1 -->
 * <button>Click me</button> <!-- tabindex will be set to 1 -->
 * <select><option>Option</option></select> <!-- tabindex will be set to 1 -->
 * ```
 */
@Directive({
  selector: 'input, select, textarea, button, a',
  standalone: true,
})
export class TabIndexDirective {
  /**
   * Creates a new TabIndexDirective instance.
   * Automatically sets the tabindex to 1 if the element's current tabindex is 0.
   * @param element The ElementRef of the host element
   */
  constructor(@Host() @Self() element: ElementRef) {
    let nativeElement = element.nativeElement as HTMLElement;
    if (nativeElement.tabIndex == 0) {
      nativeElement.tabIndex = TABINDEX;
    }
  }
}
